import { UseQueryStateResult } from '@reduxjs/toolkit/dist/query/react/buildHooks.d'
import { createApi } from '@reduxjs/toolkit/query/react'

import baseQueryInterceptor from '@api/base-query-interceptor'
import { ApiResponse } from '@api/types/api-response.type'

interface PackageOptions {
  packageListTitle: string
  floorplanOption: string
  facade: string
  facadeColor: string
  internalTheme: string
  internalColorScheme: string
}

export interface Filter {
  blockDropdownLabel: string
  levelDropdownLabel: string
  precinctDropdownLabel: string
  stageDropdownLabel: string
  toggleText: string
}

export interface FilterRemote extends Filter {
  configuration: string
}

export interface Shortlist {
  qrCodeScanLabel: string
  includeLabel: string
  agentLabel: string
}

export interface ShortlistRemote extends Shortlist {
  agentFormTitle: string
  pageTitle: string
}

export interface PropertySidePanel extends PackageOptions {
  levelStagePrefix: string
  packageListTitle: string
}

export interface PropertyView {
  panoramicButtonTitle: string
  loadExternalLinkButtonTitle: string
  closeExternalLinkButtonTitle: string
}

export interface PropertyViewRemote extends PropertyView, PackageOptions {
  externalLinkModalTitle: string
  externalLinkModalBody: string
  backButtonTitle: string
  propertyType: string
}

interface FloorStageView {
  panoramicButtonTitle: string
}

interface PageTitle {
  pageTitle: string
}

interface ZoneView {
  backButtonTitle: string
}

export interface WhiteLabel {
  showcase: {
    filter: Filter
    shortlist: Shortlist
    propertySidePanel: PropertySidePanel
    propertyView: PropertyView
    floorStageView: FloorStageView
  }
  remote: {
    areaView: PageTitle
    areaViewHouseAndLand: PageTitle
    filter: FilterRemote
    shortlist: ShortlistRemote
    zoneView: ZoneView
    propertyView: PropertyViewRemote
    floorStageView: FloorStageView
  }
}

export enum WhiteLabelType {
  Showcase = 'showcase',
  Remote = 'remote',
}

type RequestType = {
  projectName: string
  type?: WhiteLabelType
}

const VERSION = 'v3'

export const whiteLabelApi = createApi({
  reducerPath: 'whiteLabelApi',
  baseQuery: baseQueryInterceptor,
  endpoints: (builder) => ({
    getWhiteLabels: builder.query<ApiResponse<WhiteLabel>, RequestType>({
      query: ({ projectName, type }: RequestType) => ({
        url: `/${VERSION}/${projectName}/white-labels`,
        method: 'get',
        params: {
          app: type,
        },
      }),
    }),
  }),
})

export const selectFromWhiteLabelResult = ({
  data,
  isLoading,
  isError,
  status,
  error,
}: UseQueryStateResult<any, any>): {
  whiteLabelData: WhiteLabel
  isLoaded: boolean
  isError: boolean
  status: string
  errorStatus: number
} => ({
  whiteLabelData: data?.data || {},
  isLoaded: !isLoading,
  isError,
  status,
  errorStatus: error?.status,
})

export const { useGetWhiteLabelsQuery } = whiteLabelApi
